import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Droplets, Feather, Gift } from "lucide-react";
import { api, imgSrc, inr } from "@/lib/api";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import Gifting from "@/components/Gifting";

const HERO_MAIN =
  "https://images.unsplash.com/photo-1587533042502-1209d3f2d891?auto=format&fit=crop&w=1200&q=85";
const HERO_SECOND =
  "https://images.pexels.com/photos/13211211/pexels-photo-13211211.jpeg?auto=compress&cs=tinysrgb&w=800";
const STORY_IMG =
  "https://images.unsplash.com/photo-1590605095243-072811dbe64c?auto=format&fit=crop&w=1200&q=85";

const HERO_LINES = ["Objects with", "a slow soul."];

const BENTO_SPANS = [
  "relative h-72 overflow-hidden border border-line md:col-span-7 md:h-[560px]",
  "relative h-72 overflow-hidden border border-line md:col-span-5 md:h-[268px]",
  "relative h-72 overflow-hidden border border-line md:col-span-5 md:h-[268px]",
  "relative h-72 overflow-hidden border border-line md:col-span-5 md:col-start-2 md:h-64",
];

const FEATURED_SPANS = ["md:col-span-5", "md:col-span-7", "md:col-span-7", "md:col-span-5"];
const FEATURED_ASPECTS = ["aspect-[4/5]", "aspect-[16/10]", "aspect-[16/10]", "aspect-[4/5]"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yChip = useTransform(scrollYProgress, [0, 1], [0, 55]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true, limit: 4 } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
    api
      .get("/categories")
      .then((r) => setCats(r.data))
      .catch(() => {});
  }, []);

  const chipProduct = featured[2] || featured[0];

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 md:pt-36" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-16 md:px-8 md:pb-24 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="label-mono"
              data-testid="hero-eyebrow"
            >
              Hand-poured · Hand-knotted · Small batch
            </motion.p>
            <h1 className="mt-6 font-display text-6xl leading-[0.92] tracking-tight sm:text-7xl lg:text-[6.2rem]" data-testid="hero-heading">
              {HERO_LINES.map((l, i) => (
                <span key={l} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {i === 1 ? <em className="font-light italic text-terracotta">{l}</em> : l}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55 }}
              className="mt-7 max-w-md text-base leading-relaxed text-stone2"
              data-testid="hero-subcopy"
            >
              Knot &amp; Cast is a small studio making resin art, macramé and handmade goods —
              poured, knotted and finished by hand, one piece at a time.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link to="/shop" className="btn-solid" data-testid="hero-cta-shop">
                Shop the collection <ArrowRight size={16} />
              </Link>
              <a href="#story" className="btn-outline" data-testid="hero-cta-story" onClick={(e) => {
                e.preventDefault();
                document.getElementById("story")?.scrollIntoView({ behavior: "smooth" });
              }}>
                Our story
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-10 font-mono text-[11px] uppercase tracking-[0.24em] text-stone-400"
            >
              Resin Art — Macramé — Ceramics — Candles
            </motion.p>
          </div>

          <div className="relative h-[480px] sm:h-[560px] lg:col-span-6" data-testid="hero-visual">
            <motion.div style={{ y: yMain }} className="absolute right-0 top-0 h-[82%] w-[72%]">
              <div className="h-full w-full overflow-hidden rounded-t-[10rem] border border-line bg-white">
                <img
                  src={HERO_MAIN}
                  alt="Hand-poured abstract resin art"
                  className="h-full w-full object-cover"
                  data-testid="hero-image-main"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="absolute bottom-0 left-0 h-[44%] w-[46%] border-4 border-parchment shadow-[0_10px_40px_rgba(28,25,23,0.12)]"
            >
              <img
                src={HERO_SECOND}
                alt="Hand-knotted macramé wall hanging"
                className="h-full w-full object-cover"
                data-testid="hero-image-second"
              />
            </motion.div>
            {chipProduct && (
              <motion.div
                style={{ y: yChip }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.75 }}
                className="absolute right-2 top-[58%] w-56 border border-line bg-white p-4 shadow-[0_14px_40px_rgba(28,25,23,0.14)] md:right-8"
                data-testid="hero-product-chip"
              >
                <p className="label-mono">From the bench</p>
                <Link to={`/product/${chipProduct.slug}`} className="mt-2 flex items-center gap-3" data-testid="hero-chip-link">
                  <img src={imgSrc(chipProduct.images?.[0])} alt={chipProduct.name} className="h-14 w-14 border border-line object-cover" />
                  <span>
                    <span className="block font-display text-base leading-tight">{chipProduct.name}</span>
                    <span className="mt-1 block text-sm font-semibold">{inr(chipProduct.price)}</span>
                  </span>
                  <ArrowUpRight size={16} className="ml-auto shrink-0 text-terracotta" />
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <Marquee />

      {/* ---------------- BENTO CATEGORIES ---------------- */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="categories-section">
        <Reveal>
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="label-mono">The workshop shelf</p>
              <h2 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl">Browse by craft</h2>
            </div>
            <Link to="/shop" className="group inline-flex items-center gap-2 text-sm font-semibold" data-testid="categories-view-all">
              View everything
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-12">
          {cats.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08} className={BENTO_SPANS[i]}>
              <Link
                to={`/shop?category=${encodeURIComponent(c.name)}`}
                className="group absolute inset-0 block"
                data-testid={`category-tile-${i}`}
              >
                <img
                  src={imgSrc(c.cover)}
                  alt={c.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-parchment/80">
                      {c.count} {c.count === 1 ? "piece" : "pieces"}
                    </p>
                    <p className="mt-1 font-display text-3xl text-parchment">{c.name}</p>
                  </div>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-parchment/40 text-parchment transition-all duration-300 group-hover:bg-terracotta group-hover:border-terracotta">
                    <ArrowUpRight size={18} strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- FEATURED PRODUCTS ---------------- */}
      {featured.length > 0 && (
        <section className="border-y border-line bg-white/60">
          <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="featured-section">
            <Reveal>
              <div className="mb-12 max-w-xl">
                <p className="label-mono">Fresh from the bench</p>
                <h2 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl">
                  Pieces ready to ship
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-stone2">
                  A rotating pick of what is on the shelves right now. When it sells, it is gone —
                  most pieces cannot be repeated exactly.
                </p>
              </div>
            </Reveal>
            <div className="grid gap-x-8 gap-y-14 md:grid-cols-12">
              {featured.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  aspect={FEATURED_ASPECTS[i % 4]}
                  className={FEATURED_SPANS[i % 4]}
                  delay={i * 0.07}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------------- STORY ---------------- */}
      <section id="story" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-20 md:px-8 md:py-28" data-testid="story-section">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <Reveal className="relative lg:col-span-5">
            <div className="absolute -left-4 -top-4 h-full w-full border border-terracotta" aria-hidden="true" />
            <div className="relative overflow-hidden border border-line">
              <img
                src={STORY_IMG}
                alt="Hands shaping clay at the studio wheel"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
                data-testid="story-image"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="label-mono">Our story</p>
              <h2 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl">
                Made slowly, in small batches.
              </h2>
              <p className="mt-6 leading-relaxed text-stone2">
                Everything here begins at a workbench — resin poured in thin layers, cord knotted
                row by row, clay turned on the wheel. We make in small batches so every piece gets
                the hours it deserves, and nothing leaves the studio until we would happily keep it
                ourselves.
              </p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {[
                { icon: Feather, title: "One of one", text: "No factory runs. Variations in every piece are the maker's signature, not a flaw." },
                { icon: Droplets, title: "Natural materials", text: "Cotton cord, soy wax, food-safe resin and speckled stoneware — chosen to age well." },
                { icon: Gift, title: "Packed like a gift", text: "Recycled craft, tissue, a hand-written note. Gifting-ready without asking." },
              ].map((f, i) => (
                <Reveal key={f.title} delay={0.1 + i * 0.08}>
                  <div className="flex gap-5" data-testid={`story-value-${i}`}>
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-line bg-white text-terracotta">
                      <f.icon size={18} strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="font-display text-xl">{f.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-stone2">{f.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <Link to="/shop" className="btn-solid mt-10" data-testid="story-cta">
                Browse the shelf <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <Gifting />

      {/* ---------------- CTA BAND ---------------- */}
      <section className="bg-ink text-parchment" data-testid="cta-band">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="label-mono text-[#D89179]">Custom · Gifting · Bulk</p>
              <h2 className="mt-3 font-display text-4xl tracking-tight sm:text-5xl">
                Something made just for you?
              </h2>
              <p className="mt-5 max-w-lg leading-relaxed text-parchment/70">
                Custom designs, return gifts and bulk orders — resin in your colours, macramé
                sized to your wall, hampers by the dozen. Order as an enquiry at checkout or write
                to the studio; we reply within a day.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <Reveal delay={0.15}>
              <Link
                to="/shop"
                className="btn-outline border-parchment/40 text-parchment hover:bg-parchment hover:text-ink"
                data-testid="cta-band-btn"
              >
                Find something one-of-a-kind <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
